/**
 * SuperAdminPanel.tsx
 * Master control panel for the MediWard platform owner.
 * Tabs: Hospitals (approve/reject/suspend) | Invites (create/revoke)
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Stethoscope, LogOut, RefreshCw, CheckCircle, XCircle,
  PauseCircle, PlayCircle, Clock, Building2, Users,
  AlertTriangle, Loader2, LayoutDashboard, KeyRound,
  Copy, Check, Trash2, Plus, Mail, ChevronDown, ChevronRight, Layers,
  LayoutTemplate, ClipboardList, HeartPulse, Syringe, Activity,
} from 'lucide-react';
import { SPECIALTY_TEMPLATES, SpecialtyKey } from '../services/specialtyTemplates';
import { useAuth } from '../contexts/AppContext';
import {
  fetchAllHospitals, approveHospital, rejectHospital,
  toggleSuspendHospital, HospitalRow,
} from '../services/superAdminService';
import {
  fetchInvites, createInvite, deleteInvite, Invite,
} from '../services/inviteService';
import { KERALA_COLLEGES, DEPARTMENT_OPTIONS } from '../utils/colleges';
import { toast } from '../utils/toast';
import ToastContainer from './ToastContainer';
import SpecialtyDataPanel from './SpecialtyDataPanel';

// ─── Status badge ─────────────────────────────────────────────────────────────
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const map: Record<string, { label: string; cls: string; icon: React.ReactNode }> = {
    pending:   { label: 'Pending',   cls: 'bg-amber-100 text-amber-700 border-amber-200',  icon: <Clock className="w-3 h-3" /> },
    active:    { label: 'Active',    cls: 'bg-green-100 text-green-700 border-green-200',   icon: <CheckCircle className="w-3 h-3" /> },
    rejected:  { label: 'Rejected',  cls: 'bg-red-100 text-red-700 border-red-200',         icon: <XCircle className="w-3 h-3" /> },
    suspended: { label: 'Suspended', cls: 'bg-slate-100 text-slate-600 border-slate-200',  icon: <PauseCircle className="w-3 h-3" /> },
  };
  const s = map[status] ?? { label: status, cls: 'bg-slate-100 text-slate-600 border-slate-200', icon: null };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border ${s.cls}`}>
      {s.icon}{s.label}
    </span>
  );
};

// ─── Copy button ──────────────────────────────────────────────────────────────
const CopyButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      onClick={copy}
      className="p-1.5 rounded hover:bg-slate-100 transition-colors text-slate-400 hover:text-slate-600"
      title="Copy code"
    >
      {copied ? <Check className="w-3.5 h-3.5 text-green-500" /> : <Copy className="w-3.5 h-3.5" />}
    </button>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const SuperAdminPanel: React.FC<{
  onSwitchToApp?: () => void;
  onViewWorkspace?: (id: string, name: string) => void;
}> = ({ onSwitchToApp, onViewWorkspace }) => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState<'hospitals' | 'invites' | 'templates'>('hospitals');
  const [expandedTemplate, setExpandedTemplate] = useState<SpecialtyKey | null>(null);

  // ── Hospitals state ────────────────────────────────────────────────────────
  const [hospitals, setHospitals] = useState<HospitalRow[]>([]);
  const [isLoadingHospitals, setIsLoadingHospitals] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);

  const loadHospitals = useCallback(async () => {
    setIsLoadingHospitals(true);
    try {
      setHospitals(await fetchAllHospitals());
    } catch (err) {
      toast.error('Failed to load hospitals');
      console.error(err);
    } finally {
      setIsLoadingHospitals(false);
    }
  }, []);

  useEffect(() => { loadHospitals(); }, [loadHospitals]);

  const handleHospital = async (
    id: string,
    fn: () => Promise<void>,
    successMsg: string,
  ) => {
    setActionId(id);
    try {
      await fn();
      toast.success(successMsg);
      await loadHospitals();
    } catch (err) {
      toast.error(String(err));
    } finally {
      setActionId(null);
    }
  };

  // ── Invites state ──────────────────────────────────────────────────────────
  const [invites, setInvites] = useState<Invite[]>([]);
  const [isLoadingInvites, setIsLoadingInvites] = useState(false);
  const [inviteCollege, setInviteCollege] = useState('');
  const [inviteDept, setInviteDept] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [lastCreatedCode, setLastCreatedCode] = useState<string | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadInvites = useCallback(async () => {
    setIsLoadingInvites(true);
    try {
      setInvites(await fetchInvites());
    } catch (err) {
      toast.error('Failed to load invites');
      console.error(err);
    } finally {
      setIsLoadingInvites(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'invites') loadInvites();
  }, [activeTab, loadInvites]);

  const handleCreateInvite = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!inviteCollege.trim() || !inviteDept.trim()) {
      toast.error('Select a college and department first.');
      return;
    }
    setIsCreating(true);
    try {
      const newInvite = await createInvite(inviteCollege.trim(), inviteDept.trim());
      setInvites(prev => [newInvite, ...prev]);
      setLastCreatedCode(newInvite.code);
      toast.success('Invite code created');
    } catch (err) {
      toast.error(String(err));
    } finally {
      setIsCreating(false);
    }
  };

  const handleDeleteInvite = async (id: string) => {
    setDeletingId(id);
    try {
      await deleteInvite(id);
      setInvites(prev => prev.filter(i => i.id !== id));
      if (lastCreatedCode && invites.find(i => i.id === id)?.code === lastCreatedCode) {
        setLastCreatedCode(null);
      }
      toast.success('Invite revoked');
    } catch (err) {
      toast.error(String(err));
    } finally {
      setDeletingId(null);
    }
  };

  // ── Per-hospital expand/collapse ──────────────────────────────────────────
  const [expandedHospitals, setExpandedHospitals] = useState<Set<string>>(new Set());
  const toggleHospital = (id: string) =>
    setExpandedHospitals(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // Sort: pending first, then active, then others; alphabetical within each
  const sortedHospitals = [...hospitals].sort((a, b) => {
    const order: Record<string, number> = { pending: 0, active: 1, suspended: 2, rejected: 3 };
    const diff = (order[a.status] ?? 9) - (order[b.status] ?? 9);
    return diff !== 0 ? diff : a.name.localeCompare(b.name);
  });

  // Helper: clean department label
  const deptLabel = (raw?: string) =>
    raw ? raw.replace(/^DEPARTMENT\s+OF\s+/i, '').replace(/\b\w/g, c => c.toUpperCase()) : null;

  // ── Stats ──────────────────────────────────────────────────────────────────
  const pending = hospitals.filter(h => h.status === 'pending').length;
  const active  = hospitals.filter(h => h.status === 'active').length;

  const pendingInvites = invites.filter(i => !i.used).length;
  const usedInvites    = invites.filter(i => i.used).length;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* ── Header ── */}
      <header className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-3">
          <div className="bg-teal-600 p-2 rounded-lg">
            <Stethoscope className="w-5 h-5" />
          </div>
          <div>
            <h1 className="font-bold text-lg leading-none">MediWard</h1>
            <p className="text-xs text-slate-400 mt-0.5">Super Admin Console</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-sm text-slate-400 hidden sm:block">{user?.name}</span>
          <button
            onClick={() => { setLastCreatedCode(null); activeTab === 'hospitals' ? loadHospitals() : loadInvites(); }}
            className="p-2 hover:bg-slate-700 rounded-lg transition-colors"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${(isLoadingHospitals || isLoadingInvites) ? 'animate-spin' : ''}`} />
          </button>
          {onSwitchToApp && (
            <button
              onClick={onSwitchToApp}
              className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-600 hover:bg-blue-500 rounded-lg text-sm transition-colors"
            >
              <LayoutDashboard className="w-4 h-4" /> Clinical App
            </button>
          )}
          <button
            onClick={logout}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 hover:bg-slate-600 rounded-lg text-sm transition-colors"
          >
            <LogOut className="w-4 h-4" /> Sign Out
          </button>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-4 py-8">

        {/* ── Stats ── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Hospitals', value: hospitals.length,  icon: Building2,   cls: 'text-teal-600  bg-blue-50'   },
            { label: 'Pending Approval', value: pending,          icon: Clock,        cls: 'text-amber-600 bg-amber-50'  },
            { label: 'Active',           value: active,           icon: CheckCircle,  cls: 'text-green-600 bg-green-50'  },
            { label: 'Open Invites',     value: pendingInvites,   icon: KeyRound,     cls: 'text-purple-600 bg-purple-50'},
          ].map(s => (
            <div key={s.label} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 flex items-center gap-3">
              <div className={`p-2 rounded-lg ${s.cls}`}>
                <s.icon className="w-5 h-5" />
              </div>
              <div>
                <div className="text-2xl font-bold text-slate-800">{s.value}</div>
                <div className="text-xs text-slate-500">{s.label}</div>
              </div>
            </div>
          ))}
        </div>

        {/* ── Pending alert ── */}
        {pending > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-5 flex items-center gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0" />
            <p className="text-amber-700 text-sm font-medium">
              {pending} hospital{pending > 1 ? 's are' : ' is'} awaiting your approval.
            </p>
          </div>
        )}

        {/* ── Tabs ── */}
        <div className="flex gap-1 mb-6 bg-white rounded-xl p-1 shadow-sm border border-slate-100 w-fit">
          <button
            onClick={() => setActiveTab('hospitals')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'hospitals'
                ? 'bg-slate-900 text-white'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <Users className="w-4 h-4" /> Hospitals
            {pending > 0 && (
              <span className="bg-amber-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{pending}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('invites')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'invites'
                ? 'bg-slate-900 text-white'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <KeyRound className="w-4 h-4" /> Invites
            {pendingInvites > 0 && (
              <span className="bg-purple-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{pendingInvites}</span>
            )}
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${
              activeTab === 'templates'
                ? 'bg-slate-900 text-white'
                : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            <LayoutTemplate className="w-4 h-4" /> Templates
            <span className="bg-slate-200 text-slate-600 text-[10px] font-bold px-1.5 py-0.5 rounded-full leading-none">{Object.keys(SPECIALTY_TEMPLATES).length}</span>
          </button>
        </div>

        {/* ════════════════════ HOSPITALS TAB ════════════════════ */}
        {activeTab === 'hospitals' && (
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center gap-2">
              <Users className="w-4 h-4 text-slate-500" />
              <h2 className="font-semibold text-slate-800">Registered Hospitals</h2>
              <span className="text-xs text-slate-400 ml-1">{hospitals.length} total</span>
            </div>

            {isLoadingHospitals ? (
              <div className="flex justify-center items-center py-16">
                <Loader2 className="w-6 h-6 animate-spin text-blue-500" />
              </div>
            ) : hospitals.length === 0 ? (
              <div className="text-center py-16 text-slate-400">
                <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" />
                <p className="text-sm">No hospitals registered yet.</p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {sortedHospitals.map(h => {
                  const isOpen   = expandedHospitals.has(h.id);
                  const isActing = actionId === h.id;
                  const dept     = deptLabel(h.department);
                  return (
                    <div key={h.id}>
                      {/* ── Hospital row (always visible) ── */}
                      <div className="flex items-center gap-3 px-5 py-4 hover:bg-slate-50/60 transition-colors">
                        {/* Expand toggle */}
                        <button
                          onClick={() => toggleHospital(h.id)}
                          className="shrink-0 p-1 rounded hover:bg-slate-200 transition-colors text-slate-400"
                          title={isOpen ? 'Collapse' : 'Show department details'}
                        >
                          {isOpen
                            ? <ChevronDown className="w-4 h-4" />
                            : <ChevronRight className="w-4 h-4" />}
                        </button>

                        {/* Initials avatar */}
                        <div className={`shrink-0 w-9 h-9 rounded-lg flex items-center justify-center text-xs font-black ${
                          h.status === 'active'    ? 'bg-green-100 text-green-700' :
                          h.status === 'pending'   ? 'bg-amber-100 text-amber-700' :
                          h.status === 'suspended' ? 'bg-slate-100 text-slate-500' :
                                                     'bg-red-100 text-red-600'
                        }`}>
                          {h.name.slice(0, 2).toUpperCase()}
                        </div>

                        {/* Name + slug */}
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-slate-800 text-sm leading-tight">{h.name}</p>
                          {h.slug && <p className="text-[11px] text-slate-400">/{h.slug}</p>}
                        </div>

                        {/* Status + plan */}
                        <div className="hidden sm:flex items-center gap-2 shrink-0">
                          <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-slate-100 text-slate-600 uppercase">{h.plan}</span>
                          <StatusBadge status={h.status} />
                        </div>

                        {/* Actions */}
                        <div className="flex items-center gap-2 shrink-0">
                          {isActing ? (
                            <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
                          ) : (
                            <>
                              {h.status === 'pending' && (
                                <>
                                  <button onClick={() => handleHospital(h.id, () => approveHospital(h.id), `"${h.name}" approved`)}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-green-600 hover:bg-green-700 text-white text-xs font-bold rounded-lg transition-colors">
                                    <CheckCircle className="w-3.5 h-3.5" /> Approve
                                  </button>
                                  <button onClick={() => handleHospital(h.id, () => rejectHospital(h.id), `"${h.name}" rejected`)}
                                    className="flex items-center gap-1 px-3 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 text-xs font-bold rounded-lg transition-colors">
                                    <XCircle className="w-3.5 h-3.5" /> Reject
                                  </button>
                                </>
                              )}
                              {(h.status === 'active' || h.status === 'suspended') && (
                                <>
                                  {h.status === 'active' && onViewWorkspace && (
                                    <button onClick={() => { onViewWorkspace(h.id, h.name); onSwitchToApp?.(); }}
                                      className="flex items-center gap-1 px-3 py-1.5 bg-blue-100 hover:bg-blue-200 text-blue-700 text-xs font-bold rounded-lg transition-colors">
                                      <LayoutDashboard className="w-3.5 h-3.5" /> View
                                    </button>
                                  )}
                                  <button
                                    onClick={() => handleHospital(h.id, () => toggleSuspendHospital(h.id, h.status), h.status === 'suspended' ? `"${h.name}" reactivated` : `"${h.name}" suspended`)}
                                    className={`flex items-center gap-1 px-3 py-1.5 text-xs font-bold rounded-lg transition-colors ${h.status === 'suspended' ? 'bg-green-100 hover:bg-green-200 text-green-700' : 'bg-slate-100 hover:bg-slate-200 text-slate-600'}`}>
                                    {h.status === 'suspended'
                                      ? <><PlayCircle  className="w-3.5 h-3.5" /> Reactivate</>
                                      : <><PauseCircle className="w-3.5 h-3.5" /> Suspend</>}
                                  </button>
                                </>
                              )}
                            </>
                          )}
                        </div>
                      </div>

                      {/* ── Expanded: department details panel ── */}
                      {isOpen && (
                        <div className="mx-5 mb-4 rounded-xl border border-slate-200 bg-slate-50 overflow-hidden">
                          {/* Department header */}
                          <div className="flex items-center gap-2 px-4 py-3 bg-indigo-50 border-b border-indigo-100">
                            <Layers className="w-4 h-4 text-indigo-500 shrink-0" />
                            <div>
                              <p className="text-[10px] font-bold uppercase tracking-wider text-indigo-400">Registered Department</p>
                              <p className="text-sm font-bold text-indigo-900 leading-tight">
                                {dept ?? <span className="text-slate-400 font-normal italic">Not configured</span>}
                              </p>
                              {h.department && h.department !== dept && (
                                <p className="text-[10px] text-indigo-300 mt-0.5">{h.department}</p>
                              )}
                            </div>
                          </div>

                          {/* Details grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-slate-200">
                            {[
                              { label: 'Admin',      value: h.adminName  ?? '—' },
                              { label: 'Email',      value: h.adminEmail ?? '—' },
                              { label: 'Plan',       value: h.plan.toUpperCase() },
                              { label: 'Registered', value: new Date(h.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) },
                            ].map(item => (
                              <div key={item.label} className="bg-white px-4 py-3">
                                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-0.5">{item.label}</p>
                                <p className="text-xs font-semibold text-slate-700 truncate" title={item.value}>{item.value}</p>
                              </div>
                            ))}
                          </div>

                          {/* Status row */}
                          <div className="flex items-center gap-2 px-4 py-3 border-t border-slate-200">
                            <StatusBadge status={h.status} />
                            {h.trialEndsAt && (
                              <span className="text-[11px] text-slate-500">
                                Trial ends: {new Date(h.trialEndsAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                              </span>
                            )}
                            {h.slug && (
                              <span className="text-[11px] text-slate-400 ml-auto">ID: {h.slug}</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ════════════════════ INVITES TAB ════════════════════ */}
        {activeTab === 'invites' && (
          <div className="space-y-6">

            {/* Create invite form */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-6">
              <div className="flex items-center gap-2 mb-5">
                <div className="bg-purple-100 p-1.5 rounded-lg">
                  <Plus className="w-4 h-4 text-purple-600" />
                </div>
                <h2 className="font-semibold text-slate-800">Create New Invite</h2>
              </div>

              <form onSubmit={handleCreateInvite} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* College */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-600 uppercase">Medical College</label>
                    <div className="relative">
                      <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        list="sa-kerala-colleges"
                        required
                        placeholder="Type or select college…"
                        value={inviteCollege}
                        onChange={e => setInviteCollege(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm"
                      />
                      <datalist id="sa-kerala-colleges">
                        {KERALA_COLLEGES.map(c => <option key={c} value={c} />)}
                      </datalist>
                    </div>
                  </div>

                  {/* Department */}
                  <div className="space-y-1">
                    <label className="text-xs font-bold text-slate-600 uppercase">Department</label>
                    <div className="relative">
                      <Users className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
                      <input
                        list="sa-departments"
                        required
                        placeholder="Type or select department…"
                        value={inviteDept}
                        onChange={e => setInviteDept(e.target.value)}
                        className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none text-sm"
                      />
                      <datalist id="sa-departments">
                        {DEPARTMENT_OPTIONS.map(d => <option key={d} value={d} />)}
                      </datalist>
                    </div>
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={isCreating}
                  className="flex items-center gap-2 px-5 py-2.5 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm rounded-lg transition-colors disabled:opacity-70 disabled:cursor-not-allowed shadow-md shadow-purple-600/20"
                >
                  {isCreating
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <><KeyRound className="w-4 h-4" /> Generate Invite Code</>
                  }
                </button>
              </form>

              {/* Last created code banner */}
              {lastCreatedCode && (
                <div className="mt-4 bg-purple-50 border border-purple-200 rounded-xl p-4">
                  <p className="text-xs font-bold text-purple-600 uppercase mb-2 flex items-center gap-1.5">
                    <CheckCircle className="w-3.5 h-3.5" /> Invite Code Created — Share this with the department admin
                  </p>
                  <div className="flex items-center gap-3">
                    <code className="text-xl font-mono font-bold text-purple-800 tracking-widest bg-white px-4 py-2 rounded-lg border border-purple-200">
                      {lastCreatedCode}
                    </code>
                    <CopyButton text={lastCreatedCode} />
                  </div>
                  <p className="text-[11px] text-purple-500 mt-2 flex items-center gap-1">
                    <Mail className="w-3 h-3" /> Send this code to the admin via WhatsApp or email. It can only be used once.
                  </p>
                </div>
              )}
            </div>

            {/* Invites list */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
              <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <KeyRound className="w-4 h-4 text-slate-500" />
                  <h2 className="font-semibold text-slate-800">All Invites</h2>
                </div>
                <div className="flex gap-3 text-xs text-slate-500">
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-purple-400 inline-block" />{pendingInvites} pending</span>
                  <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full bg-slate-300 inline-block" />{usedInvites} used</span>
                </div>
              </div>

              {isLoadingInvites ? (
                <div className="flex justify-center items-center py-16">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-500" />
                </div>
              ) : invites.length === 0 ? (
                <div className="text-center py-16 text-slate-400">
                  <KeyRound className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No invites yet. Create one above.</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-slate-50 text-left">
                        <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Code</th>
                        <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">College</th>
                        <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Department</th>
                        <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Status</th>
                        <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Created</th>
                        <th className="px-6 py-3 text-xs font-bold text-slate-500 uppercase">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-50">
                      {invites.map(inv => (
                        <tr key={inv.id} className={`transition-colors ${inv.used ? 'opacity-50' : 'hover:bg-slate-50/60'}`}>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-1">
                              <code className="font-mono font-bold text-slate-700 tracking-wider">{inv.code}</code>
                              {!inv.used && <CopyButton text={inv.code} />}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-slate-600 max-w-[200px] truncate" title={inv.college}>
                            {inv.college}
                          </td>
                          <td className="px-6 py-4 text-slate-600">
                            {inv.department}
                          </td>
                          <td className="px-6 py-4">
                            {inv.used ? (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border bg-slate-100 text-slate-500 border-slate-200">
                                <CheckCircle className="w-3 h-3" /> Used
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold border bg-purple-100 text-purple-700 border-purple-200">
                                <Clock className="w-3 h-3" /> Pending
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 text-slate-500 text-xs">
                            {new Date(inv.createdAt).toLocaleDateString('en-IN', {
                              day: '2-digit', month: 'short', year: 'numeric',
                            })}
                          </td>
                          <td className="px-6 py-4">
                            {!inv.used && (
                              <button
                                onClick={() => handleDeleteInvite(inv.id)}
                                disabled={deletingId === inv.id}
                                className="flex items-center gap-1 px-2.5 py-1.5 bg-red-50 hover:bg-red-100 text-red-600 text-xs font-bold rounded-lg transition-colors disabled:opacity-50"
                                title="Revoke invite"
                              >
                                {deletingId === inv.id
                                  ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  : <><Trash2 className="w-3.5 h-3.5" /> Revoke</>
                                }
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ════════════════════ TEMPLATES TAB ════════════════════ */}
        {activeTab === 'templates' && (() => {
          const colorDot: Record<string, string> = {
            blue: 'bg-blue-500', emerald: 'bg-emerald-500', red: 'bg-red-500',
            violet: 'bg-violet-500', pink: 'bg-pink-500', orange: 'bg-orange-500',
            rose: 'bg-rose-500', indigo: 'bg-indigo-500', teal: 'bg-teal-500',
            cyan: 'bg-cyan-500', amber: 'bg-amber-500', purple: 'bg-purple-500',
            sky: 'bg-sky-500', slate: 'bg-slate-500',
          };
          const selected = expandedTemplate ? SPECIALTY_TEMPLATES[expandedTemplate] : null;

          const DepartmentList = () => (
            <div className="space-y-1">
              <p className="text-xs font-bold text-slate-500 uppercase tracking-wider px-1 mb-3">
                {Object.keys(SPECIALTY_TEMPLATES).length} Departments
              </p>
              {Object.values(SPECIALTY_TEMPLATES).map(t => {
                const isSelected = expandedTemplate === t.specialty;
                const fieldCount = t.fieldGroups.reduce((sum, g) => sum + g.fields.length, 0);
                return (
                  <button
                    key={t.specialty}
                    onClick={() => setExpandedTemplate(isSelected ? null : t.specialty)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition-colors ${
                      isSelected
                        ? 'bg-slate-900 text-white'
                        : 'hover:bg-white text-slate-700 hover:shadow-sm'
                    }`}
                  >
                    <div className={`w-2.5 h-2.5 rounded-full shrink-0 ${colorDot[t.color] ?? 'bg-slate-400'}`} />
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${isSelected ? 'text-white' : 'text-slate-800'}`}>{t.displayName}</p>
                      <p className={`text-[11px] ${isSelected ? 'text-slate-300' : 'text-slate-400'}`}>
                        {t.fieldGroups.length} groups · {fieldCount} fields
                      </p>
                    </div>
                    <ChevronRight className={`w-3.5 h-3.5 shrink-0 ${isSelected ? 'text-slate-300' : 'text-slate-400'}`} />
                  </button>
                );
              })}
            </div>
          );

          const DetailPanel = () => !selected ? null : (
            <div className="space-y-4">
              {/* Back button — mobile only */}
              <button
                onClick={() => setExpandedTemplate(null)}
                className="md:hidden flex items-center gap-1.5 text-sm font-semibold text-slate-600 hover:text-slate-900 mb-1"
              >
                <ChevronRight className="w-4 h-4 rotate-180" /> All Departments
              </button>

              {/* Header */}
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm px-5 py-4">
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-3 h-3 rounded-full shrink-0 ${colorDot[selected.color] ?? 'bg-slate-400'}`} />
                  <h2 className="font-bold text-slate-800 text-lg leading-tight">{selected.displayName}</h2>
                  <span className="text-xs text-slate-400 ml-auto shrink-0">{selected.specialty}</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {selected.modules.pac && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-teal-50 text-teal-700 border border-teal-200">
                      <HeartPulse className="w-3 h-3" /> PAC module · <span className="font-normal">{selected.labels.pacModule}</span>
                    </span>
                  )}
                  {selected.modules.otList && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-50 text-blue-700 border border-blue-200">
                      <ClipboardList className="w-3 h-3" /> OT List · <span className="font-normal">{selected.labels.procedureList}</span>
                    </span>
                  )}
                  {selected.modules.preOp && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-50 text-violet-700 border border-violet-200">
                      <Syringe className="w-3 h-3" /> Pre-Op checklist
                    </span>
                  )}
                  {selected.modules.podTracking && (
                    <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-50 text-green-700 border border-green-200">
                      <Activity className="w-3 h-3" /> POD tracking
                    </span>
                  )}
                  {!selected.modules.pac && !selected.modules.otList && !selected.modules.preOp && !selected.modules.podTracking && (
                    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-500 border border-slate-200">
                      Medical department — no surgical modules
                    </span>
                  )}
                </div>
              </div>

              {/* Live SpecialtyDataPanel preview — read-only, empty data */}
              {selected.fieldGroups.length > 0 ? (
                <SpecialtyDataPanel
                  key={selected.specialty}
                  fieldGroups={selected.fieldGroups}
                  data={{}}
                  canEdit={false}
                  onSave={() => {}}
                  specialtyLabel={selected.displayName}
                  color={selected.color}
                />
              ) : (
                <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">
                  <p className="text-sm">No clinical field groups defined for this department.</p>
                </div>
              )}
            </div>
          );

          return (
            <>
              {/* ── Mobile: show list OR detail (not side-by-side) ── */}
              <div className="md:hidden">
                {selected ? <DetailPanel /> : <DepartmentList />}
              </div>

              {/* ── Desktop: side-by-side ── */}
              <div className="hidden md:flex gap-6 items-start">
                <div className="w-64 shrink-0">
                  <DepartmentList />
                </div>
                <div className="flex-1 min-w-0">
                  {!selected ? (
                    <div className="flex flex-col items-center justify-center py-20 text-slate-400">
                      <LayoutTemplate className="w-10 h-10 mb-3 opacity-30" />
                      <p className="font-semibold text-slate-500">Select a department</p>
                      <p className="text-sm mt-1">to preview its clinical template UI</p>
                    </div>
                  ) : (
                    <DetailPanel />
                  )}
                </div>
              </div>
            </>
          );
        })()}

        <p className="text-center text-xs text-slate-400 mt-6">
          MediWard Platform Admin · Logged in as {user?.email}
        </p>
      </div>

      <ToastContainer />
    </div>
  );
};

export default SuperAdminPanel;
